import { getAreaOfPolygon } from 'geolib';
import { produce } from 'immer';
import { getWorkedAcres, getWorkedLocation } from '../interceptors/farmMonitor';
import { Unit } from '../helpers/unit';
import * as actionTypes from '../constants/action-types';
import { isElasticLoad } from '../helpers/common';
import { mergeFarmSummaryKPI, mergeFarmGraphKPI, mergeFarmLoadKPI, mergeDeviceSensors } from '../interceptors/farmMonitor';
import { HARVESTING_PLAN_TYPE_ID, HARVESTING_WORK_TYPE_ID } from '../constants';

const defaultAnalyseValues = {
    summary: {
        isRequesting: false,
        acres: { planned: 0, completed: 0, remaining: 0 }
    },
    graph: {
        isRequesting: false,
        planned: [],
        actual: [],
        trend: [],
        planned_activity: []
    },
    elasticWork: {
        isRequesting: false,
        payload: {
            isLive: false,
            data: null
        }
    },
    elasticLoad: {
        isRequesting: false,
        payload: {
            isLive: false,
            data: null
        }
    }
}

const initialState = {
    selectedPlanTypeId: 4,
    farmId: null,
    refreshCount: 0,
    requestIds: [],
    summaryByPlanTypeId: {},
    summaryToggleCollapsed: false,
    isDataUntilNow: false,
    fieldListById: {
        isRequesting: false,
        payload: {}
    },
    workedById: {
        isRequesting: false,
        isRequestingWorkedEG: false,
        shouldUpdateWorkedEG: false,
        egWiseResponse: {},
        fieldWiseResponse: {},
        payload: {},
    },
    fieldLoadsById: {
        isRequesting: false,
        payload: {}
    },
    egListById: {
        isRequesting: false,
        payload: {}
    },
    deviceById: {
        isRequesting: false,
        isRequestingSensorKpi: false,
        payload: {}
    }
};

export default (state = initialState, action) => {
    switch (action.type) {
        //COMMON
        case actionTypes.FARM_MONITOR_SET_FARM_ID: {
            if (!action.farmId) break;

            state = {
                ...state,
                farmId: action.farmId
            };
            break;
        }
        case actionTypes.FARM_MONITOR_SET_PLAN_TYPE_ID: {
            if (!action.payload) break;

            state = {
                ...state,
                selectedPlanTypeId: action.payload
            };
            break;
        }
        case actionTypes.FARM_MONITOR_ADD_REQUEST_ID: {
            state = {
                ...state,
                requestIds: [...state.requestIds, action.requestId]
            };
            break;
        }
        case actionTypes.FARM_MONITOR_CLEAR_REQUEST_IDS: {
            state = {
                ...state,
                requestIds: []
            };
            break;
        }
        case actionTypes.CLEAR_FARM_MONITOR: {
            state = { ...initialState, selectedPlanTypeId: state.selectedPlanTypeId };
            break;
        }
        case actionTypes.FARM_MONITOR_REFRESH: {
            state = {
                ...state,
                refreshCount: state.refreshCount + 1
            };
            break;
        }

        //WORKED DETAILS
        case actionTypes.FARM_MONITOR_GET_SUMMARY_REQUEST: {
            const { planTypeId } = action;
            if (!planTypeId) break;

            state = {
                ...state,
                summaryByPlanTypeId: {
                    ...state.summaryByPlanTypeId,
                    [planTypeId]: {
                        ...{ elasticWork: defaultAnalyseValues.elasticWork },
                        ...planTypeId === 4 && isElasticLoad() && { elasticLoad: defaultAnalyseValues.elasticLoad },
                        summary: {
                            ...defaultAnalyseValues.summary,
                            isRequesting: true,
                            ...planTypeId === 4 && (isElasticLoad() ? { load: { totalLoads: 0 } } : { load: { totalLoads: 0, loadWeight: 0 } })
                        }
                    }
                }
            }
            break;
        }
        case actionTypes.FARM_MONITOR_GET_SUMMARY: {
            const { planTypeId, payload, rangeId } = action;
            if (!planTypeId) break;

            let acres = payload && payload.acres ? payload.acres : defaultAnalyseValues.summary.acres;

            state = {
                ...state,
                summaryByPlanTypeId: {
                    ...state.summaryByPlanTypeId,
                    [planTypeId]: {
                        ...state.summaryByPlanTypeId[planTypeId],
                        rangeId,
                        summary: {
                            ...state.summaryByPlanTypeId[planTypeId].summary,
                            isRequesting: false,
                            acres: acres
                        }
                    }
                }
            }

            if (state.summaryByPlanTypeId[planTypeId].elasticWork && state.summaryByPlanTypeId[planTypeId].elasticWork.payload)
                state = {
                    ...state,
                    summaryByPlanTypeId: {
                        ...state.summaryByPlanTypeId,
                        [planTypeId]: {
                            ...state.summaryByPlanTypeId[planTypeId],
                            ...mergeFarmSummaryKPI(planTypeId, state, state.summaryByPlanTypeId[planTypeId].elasticWork.payload, defaultAnalyseValues)
                        }
                    }
                };

            if (planTypeId === 4 && state.summaryByPlanTypeId[planTypeId].elasticLoad && state.summaryByPlanTypeId[planTypeId].elasticLoad.payload)
                state = {
                    ...state,
                    summaryByPlanTypeId: {
                        ...state.summaryByPlanTypeId,
                        [planTypeId]: {
                            ...state.summaryByPlanTypeId[planTypeId],
                            ...mergeFarmLoadKPI(planTypeId, state, state.summaryByPlanTypeId[planTypeId].elasticLoad.payload)
                        }
                    }
                };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_GRAPH_REQUEST: {
            const { planTypeId } = action;
            if (!planTypeId) break;

            state = {
                ...state,
                summaryByPlanTypeId: {
                    ...state.summaryByPlanTypeId,
                    [planTypeId]: {
                        ...state.summaryByPlanTypeId[planTypeId],
                        graph: {
                            ...defaultAnalyseValues.graph,
                            isRequesting: true
                        }
                    }
                }
            }
            break;
        }
        case actionTypes.FARM_MONITOR_GET_GRAPH: {
            const { planTypeId, range } = action;
            if (!planTypeId) break;

            const payload = action.payload ? action.payload : defaultAnalyseValues.graph;

            state = {
                ...state,
                summaryByPlanTypeId: {
                    ...state.summaryByPlanTypeId,
                    [planTypeId]: {
                        ...state.summaryByPlanTypeId[planTypeId],
                        graph: {
                            ...payload,
                            isRequesting: false,
                        }
                    }
                }
            }

            if (state.summaryByPlanTypeId[planTypeId].elasticWork && state.summaryByPlanTypeId[planTypeId].elasticWork.payload)
                state = {
                    ...state,
                    summaryByPlanTypeId: {
                        ...state.summaryByPlanTypeId,
                        [planTypeId]: {
                            ...state.summaryByPlanTypeId[planTypeId],
                            ...mergeFarmGraphKPI(planTypeId, range, state, state.summaryByPlanTypeId[planTypeId].elasticWork.payload, defaultAnalyseValues, action.timezone)
                        }
                    }
                };
            break;
        }
        case actionTypes.FARM_MONITOR_LOADS_SUMMARY_REQUEST: {
            state = {
                ...state,
                summaryByPlanTypeId: {
                    ...state.summaryByPlanTypeId,
                    [HARVESTING_PLAN_TYPE_ID]: {
                        ...state.summaryByPlanTypeId[HARVESTING_PLAN_TYPE_ID],
                        summary: {
                            ...state.summaryByPlanTypeId[HARVESTING_PLAN_TYPE_ID].summary,
                            load: { totalLoads: 0, loadWeight: 0 }
                        }
                    }
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_LOADS_SUMMARY: {
            const { payload } = action;
            if (!payload) break;

            state = {
                ...state,
                summaryByPlanTypeId: {
                    ...state.summaryByPlanTypeId,
                    [HARVESTING_PLAN_TYPE_ID]: {
                        ...state.summaryByPlanTypeId[HARVESTING_PLAN_TYPE_ID],
                        summary: {
                            ...state.summaryByPlanTypeId[HARVESTING_PLAN_TYPE_ID].summary,
                            load: { totalLoads: payload.total_count, loadWeight: payload.total_capacity }
                        }
                    }
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_WORKED_KPI_REQUEST: {
            const { planTypeId, elasticType } = action;
            if (!planTypeId) break;

            state = {
                ...state,
                summaryByPlanTypeId: {
                    ...state.summaryByPlanTypeId,
                    [planTypeId]: {
                        ...state.summaryByPlanTypeId[planTypeId],
                        ...elasticType === "work" && {
                            elasticWork: {
                                ...defaultAnalyseValues.elasticWork,
                                isRequesting: true
                            }
                        },
                        ...elasticType === "load" && planTypeId === 4 && {
                            elasticLoad: {
                                ...defaultAnalyseValues.elasticLoad,
                                isRequesting: true
                            }
                        }
                    }
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_WORKED_KPI: {
            const { planTypeId, elasticType, elastic, range } = action;
            if (!planTypeId) break;
            state = {
                ...state,
                summaryByPlanTypeId: {
                    ...state.summaryByPlanTypeId,
                    [planTypeId]: {
                        ...state.summaryByPlanTypeId[planTypeId],
                        ...elasticType === "work" && {
                            elasticWork: {
                                isRequesting: false,
                                payload: {
                                    ...(state.summaryByPlanTypeId[planTypeId] && state.summaryByPlanTypeId[planTypeId].elasticWork)
                                        ? state.summaryByPlanTypeId[planTypeId].elasticWork.payload
                                        : defaultAnalyseValues.elasticWork.payload,
                                    ...elastic ? elastic : {}
                                }
                            }
                        },
                        ...planTypeId === 4 && elasticType === "load" && {
                            elasticLoad: {
                                isRequesting: false,
                                payload: {
                                    ...(state.summaryByPlanTypeId[planTypeId] && state.summaryByPlanTypeId[planTypeId].elasticLoad)
                                        ? state.summaryByPlanTypeId[planTypeId].elasticLoad.payload
                                        : defaultAnalyseValues.elasticLoad.payload,
                                    ...elastic ? elastic : {}
                                }
                            }
                        }
                    }
                }
            };

            if (!elastic) break;
            if (elasticType === "work" && elastic.data) {
                state = {
                    ...state,
                    summaryByPlanTypeId: {
                        ...state.summaryByPlanTypeId,
                        [planTypeId]: {
                            ...state.summaryByPlanTypeId[planTypeId],
                            ...mergeFarmSummaryKPI(planTypeId, state, state.summaryByPlanTypeId[planTypeId].elasticWork.payload, defaultAnalyseValues)
                        }
                    }
                };
            }

            if (planTypeId === 4 && elasticType === "load" && elastic.data) {
                state = {
                    ...state,
                    summaryByPlanTypeId: {
                        ...state.summaryByPlanTypeId,
                        [planTypeId]: {
                            ...state.summaryByPlanTypeId[planTypeId],
                            ...mergeFarmLoadKPI(planTypeId, state, state.summaryByPlanTypeId[planTypeId].elasticLoad.payload)
                        }
                    }
                };
            }

            if (state.summaryByPlanTypeId[planTypeId].elasticWork && state.summaryByPlanTypeId[planTypeId].elasticWork.payload)
                state = {
                    ...state,
                    summaryByPlanTypeId: {
                        ...state.summaryByPlanTypeId,
                        [planTypeId]: {
                            ...state.summaryByPlanTypeId[planTypeId],
                            ...mergeFarmGraphKPI(planTypeId, range, state, state.summaryByPlanTypeId[planTypeId].elasticWork.payload, defaultAnalyseValues, action.timezone)
                        }
                    }
                };
            break;
        }

        //WORKED FIELD DETAILS
        case actionTypes.FARM_MONITOR_GET_REGION_LIST_REQUEST: {
            state = {
                ...state,
                fieldListById: {
                    isRequesting: true,
                    payload: {}
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_REGION_LIST: {
            const { payload } = action;
            const unit = Unit.getInstance();

            const fieldListById = {};
            if (payload) payload.forEach(item => {
                item.shapes && item.shapes.forEach(e => {
                    const setOfLocation = (e.geo_shape_type === "polygon" && e.location) ? e.location.map(points => [points.lng, points.lat]) : [];
                    const totalAcres = unit.toArea(getAreaOfPolygon(setOfLocation) * 0.000247105, 2);
                    const regionDetails = { ...item, totalAcres };
                    delete regionDetails.shapes;
                    fieldListById[e.geo_shape_uuid] = {
                        regionId: item.geo_region_uuid,
                        regionName: item.geo_region_name,
                        shapeId: e.geo_shape_uuid,
                        shapeName: e.geo_shape_name,
                        properties: e.properties,
                        location: e.location,
                        totalAcres
                    }
                });
            });

            state = {
                ...state,
                fieldListById: {
                    isRequesting: false,
                    payload: fieldListById
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_EG_LIST_REQUEST_FOR_FIELD: {
            state = {
                ...state,
                fieldEgListById: {
                    isRequesting: action.haveRequest,
                    payload: {}
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_EG_LIST_FOR_FIELD: {
            const { payload } = action;
            const fieldEgListById = {};

            if (payload) payload.forEach(e => { fieldEgListById[e.equipment_group_uuid] = e; });
            state = {
                ...state,
                fieldEgListById: {
                    isRequesting: false,
                    payload: fieldEgListById
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_WORKED_ACRES_REQUEST: {
            state = {
                ...state,
                workedById: {
                    ...state.workedById,
                    isRequesting: true,
                    payload: {}
                }
            }
            break;
        }
        case actionTypes.FARM_MONITOR_GET_WORKED_ACRES: {
            const { payload } = action;
            const workedById = parseWorkedAcres(state, payload, state.isDataUntilNow);

            state = {
                ...state,
                workedById: {
                    ...state.workedById,
                    isRequesting: false,
                    shouldUpdateWorkedEG: true,
                    fieldWiseResponse: payload,
                    payload: payload ? workedById : initialState.workedById.payload
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_EG_WORED_ACRES_REQUEST: {
            state = {
                ...state,
                workedById: {
                    ...state.workedById,
                    isRequestingWorkedEG: true
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_EG_WORED_ACRES: {
            const { payload } = action;
            const workedById = parseEgWorkedAcres(state, state.workedById.payload, payload, state.isDataUntilNow);

            state = {
                ...state,
                workedById: {
                    ...state.workedById,
                    egWiseResponse: payload,
                    isRequestingWorkedEG: false,
                    shouldUpdateWorkedEG: false,
                    payload: workedById
                }
            };
            break;
        }
        case actionTypes.FARM_MONITOR_GET_FIELD_LOADS_REQUEST:
            state = {
                ...state,
                fieldLoadsById: {
                    isRequesting: true,
                    payload: {}
                }
            }
            break;
        case actionTypes.FARM_MONITOR_GET_FIELD_LOADS: {
            let fieldLoadsById = {};

            action.payload && action.payload.forEach(item => {
                const { shapeRefId, loads, loadCapacity } = item;

                fieldLoadsById[`${shapeRefId}_${HARVESTING_WORK_TYPE_ID}`] = {
                    ...fieldLoadsById[`${shapeRefId}_${HARVESTING_WORK_TYPE_ID}`],
                    loads: loads,
                    ...isElasticLoad() ? {} : { loadCapacity: loadCapacity }
                }
            })
            state = {
                ...state,
                fieldLoadsById: {
                    isRequesting: false,
                    payload: fieldLoadsById,
                }
            }
            break;
        }

        //Q APP
        case actionTypes.FARM_MONITOR_GET_DEVICE_LIST_REQUEST: {
            state = {
                ...state,
                deviceById: {
                    isRequesting: true,
                    payload: {}
                }
            }
            break;
        }
        case actionTypes.FARM_MONITOR_GET_DEVICE_LIST: {
            state = {
                ...state,
                deviceById: {
                    isRequesting: false,
                    payload: action.payload ? action.payload : {}
                }
            }
            break;
        }
        case actionTypes.FARM_MONITOR_GET_DEVICE_SENSORS_REQUEST: {
            state = {
                ...state,
                deviceById: {
                    ...state.deviceById,
                    isRequestingSensorKpi: true
                }
            }
            break;
        }
        case actionTypes.FARM_MONITOR_GET_DEVICE_SENSORS: {
            if (!action.payload) break;
            const deviceById = state.deviceById.payload;
            Object.keys(action.payload).forEach(deviceId => {
                if (!deviceById[deviceId]) return;
                deviceById[deviceId] = mergeDeviceSensors(deviceById[deviceId], action.payload[deviceId]);
            })

            state = {
                ...state,
                deviceById: {
                    ...state.deviceById,
                    isRequestingSensorKpi: false,
                    payload: deviceById
                }
            }
            break;
        }
        case actionTypes.FARM_MONITOR_EG_DEVICE_KPI_LIVE_MESSAGE: {
            const { deviceId, payload } = action;

            if (!payload || !deviceId) break;

            state = {
                ...state,
                deviceById: {
                    ...state.deviceById,
                    payload: {
                        ...state.deviceById.payload,
                        [deviceId]: {
                            ...state.deviceById.payload[deviceId],
                            ...payload
                        }
                    }
                }
            }
            break;
        }
        case actionTypes.FARM_MONITOR_SET_SUMMARY_TOGGLE_COLLAPSE: {
            const { payload } = action;

            state = {
                ...state,
                summaryToggleCollapsed: payload
            }
            break;
        }
        case actionTypes.FARM_MONITOR_SET_WORKED_ACRES_RANGE_TYPE: {
            const { payload } = action;
            const { fieldWiseResponse, egWiseResponse } = state.workedById;

            let workedById = parseWorkedAcres(state, fieldWiseResponse, payload);
            workedById = parseEgWorkedAcres(state, workedById, egWiseResponse, payload);

            state = produce(state, s => {
                s.isDataUntilNow = payload;
                s.workedById.shouldUpdateWorkedEG = false;
                s.workedById.payload = workedById;
            });
            break;
        }
        default:
            break;
    }
    return state;
}

const parseWorkedAcres = (state, payload, rangeType) => {

    const unit = Unit.getInstance();

    const fieldListById = state.fieldListById.payload;
    let workedById = state.workedById.payload;

    payload && payload.data && Array.isArray(payload.data) && payload.data.forEach(item => {
        const filteredIds = Object.keys(fieldListById).filter(id => fieldListById[id].regionId === item.geo_region_uuid);
        const { workedAcres } = getWorkedAcres(item, rangeType);
        const workedLocation = getWorkedLocation(item, "worked", rangeType);

        for (const shapeId of filteredIds) {
            const percentage = workedAcres > 0 ?
                unit.toFixedOf((workedAcres / fieldListById[shapeId].totalAcres) * 100, 2) : 0;

            workedById[`${shapeId}_${item.work_type_id}`] = {
                ...workedById[`${shapeId}_${item.work_type_id}`],
                workedAcres,
                workedLocation,
                percentage,
                egList: []
            };
        }
    })

    const filteredZeroListIds = Object.keys(workedById).filter(id => workedById[id].percentage === 0);
    const filteredNonZeroListIds = Object.keys(workedById).filter(id => workedById[id].percentage !== 0);

    filteredNonZeroListIds.sort((a, b) => workedById[b].percentage - workedById[a].percentage);
    const sortedListIds = [...filteredNonZeroListIds, ...filteredZeroListIds];

    let sortedListById = {};
    sortedListIds.forEach(id => sortedListById[id] = workedById[id]);

    return sortedListById;
}

const parseEgWorkedAcres = (state, workedById, payload, rangeType) => {

    const fieldListById = state.fieldListById.payload;

    payload && payload.data && Object.keys(payload.data).forEach(shapeGroupId => {
        const filteredIds = Object.keys(fieldListById).filter(id => fieldListById[id].regionId === shapeGroupId);

        const egListById = payload.data[shapeGroupId];

        Object.keys(egListById).forEach(egId => {
            const item = egListById[egId][0];
            const { workedAcres } = getWorkedAcres(item, rangeType);
            const workedLocation = getWorkedLocation(item, "worked", rangeType);

            let egDetail = {
                egId: item.equipment_group_uuid,
                workedAcres,
                workedLocation
            }

            for (const shapeId of filteredIds) {
                const egList = workedById[`${shapeId}_${item.work_type_id}`]
                    ? workedById[`${shapeId}_${item.work_type_id}`].egList : [];
                workedById[`${shapeId}_${item.work_type_id}`] = {
                    ...workedById[`${shapeId}_${item.work_type_id}`],
                    egList: [...egList, egDetail]
                };
            }
        })

    });

    return workedById;
}